import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { CheckCircle2 } from "lucide-react";

const methods = [
  { name: "ABA",     desc: "Análisis Aplicado del Comportamiento" },
  { name: "TEACCH",  desc: "Educación estructurada para el TEA" },
  { name: "Dember",  desc: "Desarrollo de habilidades comunicativas" },
  { name: "y más…",  desc: "Adaptados a cada paciente" },
];

const populations = ["Niños", "Adolescentes", "Adultos"];

export default function Methods() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      ref={ref}
      className="py-24 bg-navy overflow-hidden relative"
      aria-labelledby="methods-heading"
    >
      {/* Decorative shape */}
      <div
        aria-hidden="true"
        className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-[0.04]"
        style={{ background: "#ffffff" }}
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full opacity-[0.04]"
        style={{ background: "#5a9a8a" }}
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">

          {/* Left: text */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.55, ease: "easeOut" }}
          >
            <p className="font-sans text-xs font-semibold tracking-widest text-sage uppercase mb-4">
              Metodología
            </p>
            <h2
              id="methods-heading"
              className="font-serif font-bold text-3xl sm:text-4xl text-white leading-tight mb-6"
            >
              Utilizamos los métodos más efectivos y con respaldo científico
            </h2>
            <p className="font-sans text-base text-white/70 leading-relaxed mb-8 max-w-lg">
              Combinamos ABA, TEACCH, Dember y otros enfoques terapéuticos de vanguardia,
              adaptados siempre a las necesidades únicas de cada paciente.
            </p>

            {/* Populations */}
            <div className="flex flex-wrap gap-3" aria-label="Poblaciones que atendemos">
              {populations.map((p) => (
                <div
                  key={p}
                  className="flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-5 py-2"
                >
                  <CheckCircle2 size={15} className="text-sage" aria-hidden="true" />
                  <span className="font-sans text-sm font-medium text-white">{p}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right: method cards grid */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.55, ease: "easeOut", delay: 0.15 }}
            className="grid grid-cols-2 gap-4"
            role="list"
            aria-label="Métodos terapéuticos"
          >
            {methods.map(({ name, desc }, i) => (
              <motion.div
                key={name}
                role="listitem"
                initial={{ opacity: 0, y: 16 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.25 + i * 0.08, duration: 0.4, ease: "easeOut" }}
                className="bg-white/8 border border-white/12 rounded-2xl p-6 hover:bg-white/12 transition-colors duration-200"
              >
                <p className="font-serif font-bold text-xl text-white mb-1">{name}</p>
                <p className="font-sans text-xs text-white/55 leading-snug">{desc}</p>
              </motion.div>
            ))}
          </motion.div>

        </div>
      </div>
    </section>
  );
}
